// src/pages/Authentication/ChangePassword/index.tsx
// Main component for change password page

import React, { useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Form,
  Spinner,
} from 'reactstrap';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

// Components
import BreadCrumb from 'Components/Common/BreadCrumb';
import PasswordInput from './PasswordInput';
import PasswordRequirements from './PasswordRequirements';

// Validation
import { changePasswordSchema } from './validationSchema';

// API
import { changeUserPassword } from 'helpers/fakebackend_helper';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('authentication');

interface ChangePasswordValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Get userId from Redux Profile state or localStorage
  const profileUser = useSelector((state: any) => state.Profile?.user);
  const storedUser = localStorage.getItem('authUser');
    
  let userId = profileUser?.id || profileUser?.userId;
    
  // Fallback: Get from localStorage if Redux state is empty
  if (!userId && storedUser) {
    const parsedUser = JSON.parse(storedUser);
    userId = parsedUser?.uid || parsedUser?.id || parsedUser?.data?.id;
  }
  
  // Default fallback (should not happen if logged in)
  if (!userId) {
    userId = 1;
    console.warn('ChangePassword: userId not found, using default 1');
  }

  const validation = useFormik<ChangePasswordValues>({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: changePasswordSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Call API with currentPassword and newPassword only
        const response = await changeUserPassword(userId,{
          confirmPassword: values.confirmPassword,
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        });
        // Unwrap response if needed
        const data = response?.data || response;

        // Success
        toast.success(st('doi_mat_khau_thanh_cong_message'));
        
        // Reset form
        validation.resetForm();
        
        // Optional: Navigate back to profile or logout
        setTimeout(() => {
          navigate('/profile');
        }, 1500);
      } catch (error: any) {
        // Error handling
        const errorMsg =
          error?.response?.data?.message ||
          error?.message ||
          st('co_loi_xay_ra_khi_doi_text');
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleCancel = () => {
    navigate(-1); // Go back
  };

  document.title = st('doi_mat_khau_text');

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('doi_mat_khau_text')} pageTitle={st('tai_khoan_text')} />

          <Row className="justify-content-center">
            <Col lg={6} md={8}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">
                    <i className="ri-lock-password-line me-2"></i>
                    {st('doi_mat_khau_text')}
                  </h5>
                </CardHeader>

                <CardBody className="p-4">
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    {/* Current Password */}
                    <PasswordInput
                      name="currentPassword"
                      label={st('mat_khau_hien_tai_label')}
                      placeholder={st('nhap_mat_khau_hien_tai_placeholder')}
                      value={validation.values.currentPassword}
                      error={validation.errors.currentPassword}
                      touched={validation.touched.currentPassword}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                    />

                    {/* New Password */}
                    <PasswordInput
                      name="newPassword"
                      label={st('mat_khau_moi_label')}
                      placeholder={st('toi_thieu_8_ky_tu_co_placeholder')}
                      value={validation.values.newPassword}
                      error={validation.errors.newPassword}
                      touched={validation.touched.newPassword}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                    />

                    {/* Confirm Password */}
                    <PasswordInput
                      name="confirmPassword"
                      label={st('xac_nhan_mat_khau_moi_label')}
                      placeholder={st('nhap_lai_mat_khau_moi_placeholder')}
                      value={validation.values.confirmPassword}
                      error={validation.errors.confirmPassword}
                      touched={validation.touched.confirmPassword}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                    />

                    {/* Password Requirements Box */}
                    <PasswordRequirements />

                    {/* Action Buttons */}
                    <div className="d-flex gap-2 justify-content-end mt-4">
                      <Button
                        type="button"
                        color="light"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        <i className="ri-close-line me-1"></i>
                        {st('huy_bo_button')}
                      </Button>
                      <Button
                        type="submit"
                        color="primary"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            {st('dang_xu_ly_text')}
                          </>
                        ) : (
                          <>
                            <i className="ri-save-line me-1"></i>
                            {st('cap_nhat_mat_khau_text')}
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ChangePassword;